import type { Deal } from "../types";

export const REPLY_CLOCK_HOURS = 48;
export const DUE_SOON_HOURS = 12;

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** UAE time is fixed UTC+4 (no daylight saving). */
const UAE_OFFSET_MS = 4 * HOUR_MS;

export type ReplyClockState =
  | "not-started"
  | "running"
  | "due-soon"
  | "overdue"
  | "stopped"
  | "closed";

export type ReplyClock = {
  state: ReplyClockState;
  /** received + 48 working hours; null when there is no received date. */
  deadline: Date | null;
  /** Working hours left (negative when overdue, frozen at the reply when stopped); null when not started or closed. */
  hoursLeft: number | null;
};

export type ReplyClockInput = {
  received_at?: string | null;
  first_reply_at?: string | null;
  stage?: Deal["stage"] | null;
};

/*
 * All arithmetic below runs on "UAE wall-clock" milliseconds: the UTC epoch
 * shifted by +4h, so UTC getters read the UAE date. This makes results
 * independent of the process or browser timezone.
 */

const toUae = (iso: string) => new Date(iso).getTime() + UAE_OFFSET_MS;

/** Holidays as whole-day indices (days since epoch), so loops avoid Date objects. */
type HolidayDays = ReadonlySet<number>;

const toHolidayDays = (holidays: ReadonlySet<string>): HolidayDays =>
  new Set(
    Array.from(holidays, (date) => Math.floor(Date.parse(date) / DAY_MS)),
  );

const isWorkingDay = (uaeMs: number, holidays: HolidayDays) => {
  const day = Math.floor(uaeMs / DAY_MS);
  // Epoch day 0 (1970-01-01) was a Thursday; 0 = Sunday, 6 = Saturday.
  const weekday = (day + 4) % 7;
  return weekday !== 0 && weekday !== 6 && !holidays.has(day);
};

const nextMidnight = (uaeMs: number) =>
  (Math.floor(uaeMs / DAY_MS) + 1) * DAY_MS;

/** Moves a non-working instant to 00:00 of the next working day. */
const alignToWorking = (uaeMs: number, holidays: HolidayDays) => {
  let t = uaeMs;
  while (!isWorkingDay(t, holidays)) t = nextMidnight(t);
  return t;
};

/** Working milliseconds between two UAE instants (start <= end assumed). */
const workedBetween = (
  startUae: number,
  endUae: number,
  holidays: HolidayDays,
) => {
  let t = alignToWorking(startUae, holidays);
  let worked = 0;
  while (t < endUae) {
    const dayEnd = nextMidnight(t);
    if (isWorkingDay(t, holidays)) worked += Math.min(dayEnd, endUae) - t;
    t = dayEnd;
  }
  return worked;
};

/** UAE instant reached after `hours` of working time starting at `startUae`. */
const addWorkingHours = (
  startUae: number,
  hours: number,
  holidays: HolidayDays,
) => {
  let t = alignToWorking(startUae, holidays);
  let remaining = hours * HOUR_MS;
  for (;;) {
    const available = nextMidnight(t) - t;
    if (remaining <= available) return t + remaining;
    remaining -= available;
    t = alignToWorking(nextMidnight(t), holidays);
  }
};

/**
 * Computes the 48-working-hour first-reply clock of an enquiry.
 * Working time is Monday to Friday (UAE time, UTC+4) minus public holidays,
 * counted as clock hours. Pure: pass `now` explicitly.
 *
 * @param enquiry received_at, first_reply_at and stage of the enquiry
 * @param holidays public holiday dates as `YYYY-MM-DD`
 * @param now the instant to evaluate at
 */
export const computeReplyClock = (
  enquiry: ReplyClockInput,
  holidays: ReadonlySet<string>,
  now: Date,
): ReplyClock => {
  const { received_at, first_reply_at, stage } = enquiry;
  if (!received_at) {
    return { state: "not-started", deadline: null, hoursLeft: null };
  }
  const holidayDays = toHolidayDays(holidays);
  const start = toUae(received_at);
  const deadline = new Date(
    addWorkingHours(start, REPLY_CLOCK_HOURS, holidayDays) - UAE_OFFSET_MS,
  );
  if (stage === "won" || stage === "lost") {
    return { state: "closed", deadline, hoursLeft: null };
  }
  const stopped = Boolean(first_reply_at);
  const end = stopped
    ? toUae(first_reply_at as string)
    : now.getTime() + UAE_OFFSET_MS;
  const elapsed = end > start ? workedBetween(start, end, holidayDays) : 0;
  const hoursLeft = REPLY_CLOCK_HOURS - elapsed / HOUR_MS;
  let state: ReplyClockState = "running";
  if (stopped) state = "stopped";
  else if (hoursLeft <= 0) state = "overdue";
  else if (hoursLeft < DUE_SOON_HOURS) state = "due-soon";
  return { state, deadline, hoursLeft };
};
